import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

interface VersionSelectionContextValue {
  selectedVersions: Record<string, string>;
  getSelectedVersion: (scriptId: string) => string | undefined;
  setSelectedVersion: (scriptId: string, versionId: string | null) => void;
  resetVersion: (scriptId: string) => void;
  clearAll: () => void;
}

const VersionSelectionContext = createContext<VersionSelectionContextValue | null>(null);

const STORAGE_KEY = "story-gauge-version-selections";

const loadFromStorage = (): Record<string, string> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const saveToStorage = (data: Record<string, string>) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Silently fail if localStorage is not available
  }
};

export const VersionSelectionProvider = ({ children }: { children: ReactNode }) => {
  const [selectedVersions, setSelectedVersions] = useState<Record<string, string>>(loadFromStorage);

  // Persist to localStorage whenever selections change
  useEffect(() => {
    saveToStorage(selectedVersions);
  }, [selectedVersions]);

  const getSelectedVersion = useCallback(
    (scriptId: string) => selectedVersions[scriptId],
    [selectedVersions]
  );

  const setSelectedVersion = useCallback((scriptId: string, versionId: string | null) => {
    setSelectedVersions((previous) => {
      if (!versionId) {
        if (!(scriptId in previous)) return previous;
        const { [scriptId]: _removed, ...rest } = previous;
        return rest;
      }

      if (previous[scriptId] === versionId) {
        return previous;
      }

      return { ...previous, [scriptId]: versionId };
    });
  }, []);

  const resetVersion = useCallback((scriptId: string) => {
    setSelectedVersions((previous) => {
      if (!(scriptId in previous)) return previous;
      const { [scriptId]: _removed, ...rest } = previous;
      return rest;
    });
  }, []);

  const clearAll = useCallback(() => {
    setSelectedVersions({});
  }, []);

  const value = useMemo<VersionSelectionContextValue>(
    () => ({ selectedVersions, getSelectedVersion, setSelectedVersion, resetVersion, clearAll }),
    [selectedVersions, getSelectedVersion, setSelectedVersion, resetVersion, clearAll]
  );

  return (
    <VersionSelectionContext.Provider value={value}>
      {children}
    </VersionSelectionContext.Provider>
  );
};

export const useVersionSelection = () => {
  const context = useContext(VersionSelectionContext);
  if (!context) {
    throw new Error("useVersionSelection must be used within a VersionSelectionProvider");
  }
  return context;
};

export const useScriptVersionSelection = (scriptId: string | null | undefined) => {
  const { getSelectedVersion, setSelectedVersion, resetVersion } = useVersionSelection();

  const selectedVersionId = scriptId ? getSelectedVersion(scriptId) : undefined;

  const updateSelectedVersion = useCallback(
    (versionId: string | null) => {
      if (!scriptId) return;
      setSelectedVersion(scriptId, versionId);
    },
    [scriptId, setSelectedVersion]
  );

  const clearSelectedVersion = useCallback(() => {
    if (!scriptId) return;
    resetVersion(scriptId);
  }, [scriptId, resetVersion]);

  return {
    selectedVersionId,
    setSelectedVersion: updateSelectedVersion,
    resetSelectedVersion: clearSelectedVersion,
  };
};
